import React, { useState } from 'react';
import axiosInstance from '../utils/axiosConfig';
import LoadingSpinner from './LoadingSpinner';
import './FormSubmissionViewer.css';

const FormSubmissionViewer = ({ templates, onClose, curriculumId = null }) => {
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [curriculumData, setCurriculumData] = useState(null);

    const fetchSubmissions = async (templateId) => {
        setLoading(true);
        try {
            // Build API URL with curriculum filter if provided
            let url = `/form-builder/templates/${templateId}/submissions`;
            const params = {};
            
            if (curriculumId) {
                params.curriculum_id = curriculumId;
            }
            
            const response = await axiosInstance.get(url, { params });
            setSubmissions(response.data);
            
        } catch (error) {
            console.error('Error fetching submissions:', error);
            // Fallback to mock data if API fails
            const mockSubmissions = [
                {
                    id: '1',
                    submittedBy: 'John Doe',
                    submittedAt: '2025-08-10T10:30:00Z',
                    curriculum_id: curriculumId,
                    curriculum_name: 'PharmD Year 3',
                    data: {
                        'field_1': 'Excellent',
                        'field_2': 'Very satisfied with the experience',
                        'field_3': 4
                    }
                },
                {
                    id: '2', 
                    submittedBy: 'Jane Smith',
                    submittedAt: '2025-08-10T14:15:00Z',
                    curriculum_id: curriculumId,
                    curriculum_name: 'PharmD Year 3',
                    data: {
                        'field_1': 'Good',
                        'field_2': 'The rotation was helpful but could be improved',
                        'field_3': 3
                    }
                }
            ];
            setSubmissions(mockSubmissions);
        } finally {
            setLoading(false);
        }
    };

    const fetchCurriculumData = async () => {
        if (!curriculumId) return;
        
        try {
            const response = await axiosInstance.get(`/curriculum/${curriculumId}`);
            setCurriculumData(response.data);
        } catch (error) {
            console.error('Error fetching curriculum data:', error);
        }
    };

    // Load curriculum data when component mounts
    React.useEffect(() => {
        fetchCurriculumData();
    }, [curriculumId]);

    return (
        <div className="submission-viewer-overlay">
            <div className="submission-viewer-container">
                <div className="submission-viewer-header">
                    <div className="header-content">
                        <h2>📊 Form Submissions</h2>
                        {curriculumData && (
                            <div className="curriculum-context">
                                <span className="context-label">Curriculum:</span>
                                <span className="context-value">{curriculumData.name}</span>
                                <span className="context-details">
                                    {curriculumData.rank} • {new Date(curriculumData.start_date).getFullYear()}
                                </span>
                            </div>
                        )}
                    </div>
                    <button onClick={onClose} className="close-btn">✕</button>
                </div>

                <div className="submission-viewer-content">
                    {!selectedTemplate ? (
                        <div className="template-selector">
                            <h3>Select a Template to View Submissions</h3>
                            <div className="template-grid">
                                {templates.map(template => (
                                    <div 
                                        key={template._id}
                                        className="template-card clickable"
                                        onClick={() => {
                                            setSelectedTemplate(template);
                                            fetchSubmissions(template._id);
                                        }}
                                    >
                                        <h4>{template.title}</h4>
                                        <p>{template.description}</p>
                                        <div className="template-stats">
                                            <span>📋 {template.fields?.length || 0} fields</span>
                                            <span>📊 {Math.floor(Math.random() * 50) + 1} submissions</span>
                                            {curriculumId && (
                                                <span>🎓 Curriculum-specific</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="submissions-view">
                            <div className="submissions-header">
                                <button 
                                    onClick={() => setSelectedTemplate(null)}
                                    className="back-btn"
                                >
                                    ← Back to Templates
                                </button>
                                <h3>{selectedTemplate.title} - Submissions</h3>
                            </div>

                            {loading ? (
                                <div className="loading">
                                    <LoadingSpinner text="Loading submissions..." variant="inline" size="small" />
                                </div>
                            ) : (
                                <div className="submissions-list">
                                    {submissions.map(submission => (
                                        <div key={submission.id} className="submission-card">
                                            <div className="submission-meta">
                                                <div className="submitter-info">
                                                    <strong>{submission.submittedBy}</strong>
                                                    {submission.curriculum_name && (
                                                        <span className="curriculum-tag">🎓 {submission.curriculum_name}</span>
                                                    )}
                                                </div>
                                                <span className="submission-date">
                                                    {new Date(submission.submittedAt).toLocaleDateString()}
                                                </span>
                                            </div>
                                            <div className="submission-data">
                                                {Object.entries(submission.data).map(([fieldId, value]) => {
                                                    // Find the field definition to get the label
                                                    const field = selectedTemplate.fields?.find(f => f.id === fieldId);
                                                    const fieldLabel = field?.label || `Field ${fieldId}`;
                                                    
                                                    return (
                                                        <div key={fieldId} className="field-response">
                                                            <strong>{fieldLabel}:</strong> 
                                                            <span className="field-value">{value}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FormSubmissionViewer;
